/**
 * DQ Workbench shell - one header + tab strip across the four tabs
 * (Overview / FP Grid / Trending / Reports). Tabs are react-router links so
 * right-click / ctrl-click "open in new tab" works (MANDATORY nav rule); the
 * active tab carries the teal underline. Market state lives in `?market=` and
 * is shared with every tab via DataExceptionsProvider.
 */
import React, { useCallback, useMemo, useState } from 'react'
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Link as RouterLink, Outlet, useLocation, useSearchParams } from 'react-router-dom'

import AdminPageHeader from '../../../components/admin/shared/AdminPageHeader'
import { ALL_MARKETS } from '../../../constants/markets'
import { poolwerxColors } from '../../../theme/colors'
import { DataExceptionsProvider } from './DataExceptionsContext'

const BASE = '/admin/data-exceptions'

const TABS = [
  { label: 'Overview', path: BASE },
  { label: 'FP Grid', path: `${BASE}/grid` },
  { label: 'Trending', path: `${BASE}/trending` },
  { label: 'Notes', path: `${BASE}/notes` },
  { label: 'Reports', path: `${BASE}/reports` },
]

const SUBTITLES: Record<string, string> = {
  [BASE]: 'The shared DQ queue - what to review, who cleared what, what is flaring up.',
  [`${BASE}/grid`]: 'Every franchise against every check - the eye finds the hot cells.',
  [`${BASE}/trending`]: 'Early warning by rate of change - a small FP flaring counts as loudly as a big one.',
  [`${BASE}/notes`]: 'Open exceptions with an active note thread - read, reply, and act in one place.',
  [`${BASE}/reports`]: 'Read-first, shareable, market-segmentable posture for the period.',
}

const DataExceptionsLayout: React.FC = () => {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [refreshKey, setRefreshKey] = useState(0)

  const market = searchParams.get('market') || ''

  const setMarket = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value) next.set('market', value)
          else next.delete('market')
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), [])

  // Active tab = the longest matching path (so /grid doesn't match the BASE
  // index). Preserve the current query string on each tab link.
  const search = location.search
  const activePath = useMemo(() => {
    const matches = TABS.filter((t) => location.pathname === t.path)
    return matches.length ? matches[0].path : BASE
  }, [location.pathname])

  const ctxValue = useMemo(
    () => ({ market, setMarket, refreshKey, refresh }),
    [market, setMarket, refreshKey, refresh],
  )

  return (
    <Box>
      <AdminPageHeader
        pageKey="dataExceptions"
        descriptionOverride={SUBTITLES[activePath] || SUBTITLES[BASE]}
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Market</InputLabel>
              <Select label="Market" value={market} onChange={(e) => setMarket(e.target.value)}>
                <MenuItem value="">All markets</MenuItem>
                {ALL_MARKETS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Refresh">
              <IconButton onClick={refresh} aria-label="Refresh">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      <Tabs
        value={activePath}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTabs-indicator': { backgroundColor: poolwerxColors.teal },
          '& .Mui-selected': { color: `${poolwerxColors.teal} !important` },
        }}
      >
        {TABS.map((t) => (
          <Tab
            key={t.path}
            value={t.path}
            label={t.label}
            component={RouterLink}
            to={{ pathname: t.path, search }}
          />
        ))}
      </Tabs>

      <DataExceptionsProvider value={ctxValue}>
        <Outlet />
      </DataExceptionsProvider>
    </Box>
  )
}

export default DataExceptionsLayout
